/**
 * THE TMS PUSH EXTENSION'S RESULT, AS THE API'S REPORT ACCEPTS IT - one reading of it, shared by
 * the two programs that handle it before the API does: the extension, which makes it (and checks
 * every result its confirm page hands the background), and the Control Centre page, which carries
 * it to `POST /appointments/push/report` unchanged.
 *
 * Authority: `12-tms-push-extension.md` section 6 (the message) and section 6a (how the API
 * validates it); the API as built, `parseExtensionResult` in
 * `apps/api/src/routes/appointments-push-extension.ts` (commit 1904b90). This module mirrors that
 * function RULE FOR RULE, and a parity test in the API's workspace
 * (`appointments-push-extension.result-parity.test.ts`) holds the two to the same answer for every
 * message it tries - so a result the page or the extension accepts is one the API accepts, and
 * one they refuse is one the API would answer 400. Shared never depends on the API, which is why
 * the rules are written out here rather than imported.
 *
 * THE RULES (the API's, in its order)
 *
 *   * the message is an object with exactly `type`, `version`, `pressId`, `cancelled`, `results`,
 *     `collateral`, `postsMade`, `transport` and `extensionVersion` - every one present, no other;
 *   * `type` "hockeytd.tms-push.result", `version` 1;
 *   * `pressId` a client-minted uuid (versions 1-8, the RFC variant, any case), and the press
 *     asked about (compared lower-cased, as the API lower-cases both);
 *   * `cancelled` true or false; `postsMade` 0 or 1;
 *   * `results` an array of at most 64, each exactly `{ tmsMatchDbId, outcome, detail, at,
 *     readBack }` plus an optional `reason`: a safe integer id; `outcome` "accepted_draft" or
 *     "failed"; `reason` one of the gateway's five when failed, absent or null when accepted; `at`
 *     an ISO 8601 instant with its zone, at most 40 characters, naming a real calendar day as the
 *     API's own `isCalendarDate` reads one; `detail` a string of at most 2000 characters that is
 *     not blank; `readBack` null or exactly `{ TO, SJ, TJ }`, each a string of at most 64;
 *   * no match named twice;
 *   * `collateral` an array of at most 100 strings of at most 300 characters;
 *   * `extensionVersion` digits and dots, at most 32 characters, and `transport` exactly
 *     `chrome-extension/{extensionVersion}`;
 *   * a cancelled result counts no post and accepts no match.
 *
 * One rule more, which the API applies once it has read the press (a 400 too) and this module
 * applies only when told the press's owed matches: every match named must be one the press owes.
 *
 * Nothing is repaired. A message is accepted as it came - the very object - or refused, with the
 * first reason in words.
 */
/* -- The words and the bounds (the API's own) ----------------------------------------------------- */
export const TMS_PUSH_RESULT_TYPE = "hockeytd.tms-push.result";
export const TMS_PUSH_RESULT_VERSION = 1;
/** The API's `EXTENSION_RESULT_LIMITS`, verbatim. */
export const TMS_PUSH_RESULT_LIMITS = {
    detail: 2_000,
    readBack: 64,
    at: 40,
    results: 64,
    collateral: 100,
    collateralItem: 300,
    extensionVersion: 32,
};
/** The gateway's five failure words (migration 060's CHECK), in the API's order. */
export const TMS_PUSH_FAILURE_WORDS = [
    "transport_unavailable",
    "session_expired",
    "unreachable",
    "form_changed",
    "rejected_by_tms",
];
/* -- The rules ---------------------------------------------------------------------------------------- */
const MESSAGE_KEYS = [
    "type",
    "version",
    "pressId",
    "cancelled",
    "results",
    "collateral",
    "postsMade",
    "transport",
    "extensionVersion",
];
const ENTRY_KEYS = ["tmsMatchDbId", "outcome", "detail", "at", "readBack"];
const READ_BACK_KEYS = ["TO", "SJ", "TJ"];
/** The API's press id: a client-minted uuid, versions 1-8, the RFC variant. */
const PRESS_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** The API's `ISO_INSTANT`: an ISO 8601 instant WITH its zone. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)$/;
/** The API's `EXTENSION_VERSION`: digits and dots. */
const EXTENSION_VERSION = /^[0-9]+(?:\.[0-9]+)*$/;
class Refusal extends Error {
}
function refuse(problem) {
    throw new Refusal(problem);
}
function isObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** The API's `exactObject`: every required key its own, no key that is neither required nor optional. */
function exact(value, where, required, optional = []) {
    if (!isObject(value))
        refuse(`${where} is not an object`);
    const allowed = [...required, ...optional];
    for (const key of Object.keys(value)) {
        if (!allowed.includes(key))
            refuse(`${where} has a field the API does not know: "${key.slice(0, 40)}"`);
    }
    for (const key of required) {
        if (!Object.prototype.hasOwnProperty.call(value, key))
            refuse(`${where} is missing ${key}`);
    }
    return value;
}
/** The API's `boundedString`. */
function bounded(value, where, max, nonBlank) {
    if (typeof value !== "string" || value.length > max || (nonBlank && value.trim() === "")) {
        refuse(`${where} is not ${nonBlank ? "a non-blank" : "a"} string of at most ${String(max)} characters`);
    }
    return value;
}
/** The API's `isCalendarDate` (routes/query.ts), exactly: `Date.UTC` must give the same day back. */
function isApiCalendarDate(value) {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (parts === null)
        return false;
    const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
    const probe = new Date(Date.UTC(year, month - 1, day));
    return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}
/** The API's `isRecordableTime`, with the 40-character bound the report route puts before it. */
function isRecordableInstant(value) {
    return (typeof value === "string" &&
        value.length <= TMS_PUSH_RESULT_LIMITS.at &&
        ISO_INSTANT.test(value) &&
        Number.isFinite(Date.parse(value)) &&
        isApiCalendarDate(value.slice(0, 10)));
}
function isFailureWord(value) {
    return typeof value === "string" && TMS_PUSH_FAILURE_WORDS.includes(value);
}
/** Whether `value` is an extension version the API accepts: digits and dots, at most 32 characters. */
export function isTmsPushExtensionVersion(value) {
    return typeof value === "string" && value.length <= TMS_PUSH_RESULT_LIMITS.extensionVersion && EXTENSION_VERSION.test(value);
}
/** Whether `value` is a press id the API accepts: a client-minted uuid. */
export function isTmsPushPressId(value) {
    return typeof value === "string" && PRESS_ID.test(value);
}
function entryOf(raw, where) {
    const value = exact(raw, where, ENTRY_KEYS, ["reason"]);
    const tmsMatchDbId = value["tmsMatchDbId"];
    if (typeof tmsMatchDbId !== "number" || !Number.isSafeInteger(tmsMatchDbId))
        refuse(`${where}.tmsMatchDbId is not a TMS match id`);
    const outcome = value["outcome"];
    if (outcome !== "accepted_draft" && outcome !== "failed")
        refuse(`${where}.outcome is not "accepted_draft" or "failed"`);
    const reason = value["reason"];
    if (outcome === "failed" && !isFailureWord(reason))
        refuse(`${where}.reason is not one of ${TMS_PUSH_FAILURE_WORDS.join(", ")}`);
    if (outcome === "accepted_draft" && reason !== undefined && reason !== null)
        refuse(`${where}.reason is given on an acceptance`);
    if (!isRecordableInstant(value["at"]))
        refuse(`${where}.at is not an ISO 8601 instant with its zone of at most ${String(TMS_PUSH_RESULT_LIMITS.at)} characters`);
    bounded(value["detail"], `${where}.detail`, TMS_PUSH_RESULT_LIMITS.detail, true);
    const readBack = value["readBack"];
    if (readBack !== null) {
        const crew = exact(readBack, `${where}.readBack`, READ_BACK_KEYS);
        for (const slot of READ_BACK_KEYS)
            bounded(crew[slot], `${where}.readBack.${slot}`, TMS_PUSH_RESULT_LIMITS.readBack, false);
    }
    return raw;
}
function check(raw, pressId, owed) {
    const value = exact(raw, "the result", MESSAGE_KEYS);
    if (value["type"] !== TMS_PUSH_RESULT_TYPE)
        refuse(`the result's type is not "${TMS_PUSH_RESULT_TYPE}"`);
    if (value["version"] !== TMS_PUSH_RESULT_VERSION)
        refuse(`the result's version is not ${String(TMS_PUSH_RESULT_VERSION)}`);
    const resultPress = value["pressId"];
    if (!isTmsPushPressId(resultPress))
        refuse("the result names no press");
    if (resultPress.toLowerCase() !== String(pressId).toLowerCase())
        refuse("the result is for another press");
    if (typeof value["cancelled"] !== "boolean")
        refuse("the result does not say whether it was cancelled");
    const postsMade = value["postsMade"];
    if (postsMade !== 0 && postsMade !== 1)
        refuse("the result counts other than 0 or 1 posts");
    const results = value["results"];
    if (!Array.isArray(results) || results.length > TMS_PUSH_RESULT_LIMITS.results) {
        refuse(`the result does not carry at most ${String(TMS_PUSH_RESULT_LIMITS.results)} match results`);
    }
    const entries = results.map((entry, index) => entryOf(entry, `results[${String(index)}]`));
    const seen = new Set();
    for (const entry of entries) {
        if (seen.has(entry.tmsMatchDbId))
            refuse(`the result names match ${String(entry.tmsMatchDbId)} twice`);
        seen.add(entry.tmsMatchDbId);
    }
    const collateral = value["collateral"];
    if (!Array.isArray(collateral) || collateral.length > TMS_PUSH_RESULT_LIMITS.collateral) {
        refuse(`the collateral warnings are not a list of at most ${String(TMS_PUSH_RESULT_LIMITS.collateral)}`);
    }
    collateral.forEach((item, index) => bounded(item, `collateral[${String(index)}]`, TMS_PUSH_RESULT_LIMITS.collateralItem, false));
    const version = value["extensionVersion"];
    if (!isTmsPushExtensionVersion(version))
        refuse("the result's extensionVersion is not digits and dots");
    if (value["transport"] !== `chrome-extension/${version}`)
        refuse(`the result's transport is not "chrome-extension/${version}"`);
    if (value["cancelled"] === true) {
        if (postsMade !== 0)
            refuse("the result is cancelled and counts a post");
        if (entries.some((entry) => entry.outcome === "accepted_draft"))
            refuse("the result is cancelled and accepts a match");
    }
    if (owed !== null) {
        const stranger = entries.find((entry) => !owed.includes(entry.tmsMatchDbId));
        if (stranger !== undefined)
            refuse(`the result names match ${String(stranger.tmsMatchDbId)}, which this press does not owe`);
    }
    return raw;
}
/**
 * Read one result message for press `pressId`, exactly as the API's report reads it - and, given
 * the press's owed match ids, as the report reads it once it has found the press. The message that
 * is accepted is the very object that was read.
 */
export function readTmsPushResultMessage(raw, pressId, owed = null) {
    try {
        return { ok: true, message: check(raw, pressId, owed) };
    }
    catch (error) {
        if (error instanceof Refusal)
            return { ok: false, problem: error.message };
        return { ok: false, problem: "the result could not be read" };
    }
}
//# sourceMappingURL=tms-push-result.js.map